import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import api from './services/api';
import { useAuth } from './contexts/AuthContext';
import { HeaderHeightContext, CurrentJobContext } from './contexts/LayoutContext';
import LoginPage from './pages/LoginPage';
import TimesheetsPage from './pages/TimesheetsPage';
import TimesheetDetailPage from './pages/TimesheetDetailPage';
import ExpensesPage from './pages/ExpensesPage';
import SettingsPage from './pages/SettingsPage';
import HelpPage from './pages/TrainingGuidePage';
import InvoicingHelpPage from './pages/InvoicingHelpPage';
import JobPlanningLinesView from './pages/JobPlanningLinesView';
import {
  CreateInvoicesPage, InvoiceApprovalPage, JobBudgetPage, ReceivablesPage,
  ProjectionsPage, ProjectionsAdminPage, NavisionWebRedirect,
} from './pages/JobsPages';
import JobPage from './pages/JobPage';
import JobListPage from './pages/JobListPage';
import ReportsPage from './pages/ReportsPage';
import AppHeader from './components/AppHeader';
import DashboardView, { dashboardPanelsFor } from './components/DashboardView';
import BillableTime from './components/BillableTime';
import { SECTIONS, homePath, sectionLanding } from './nav/sections';
import { timesheetPath } from './nav/paths';

// "/" opens the user's home screen; old /?tab=... links open the matching screen.
const LEGACY_HOME_TABS = { advanced: '/settings', timesheets: '/time/timesheets', billable: '/analytics/billable', dashboard: '/dashboard' };
function HomeRedirect() {
  const { user } = useAuth();
  const tab = new URLSearchParams(useLocation().search).get('tab');
  return <Navigate replace to={LEGACY_HOME_TABS[tab] || homePath(user)} />;
}

function LegacyTimesheetRedirect() {
  const { id } = useParams();
  return <Navigate replace to={timesheetPath(id)} />;
}

// A different timesheet is a fresh screen.
function TimesheetDetailRoute() {
  const { id } = useParams();
  return <TimesheetDetailPage key={id} />;
}

// A section's own address opens its first screen the user can use.
function SectionLanding({ section }) {
  const { user } = useAuth();
  return <Navigate replace to={sectionLanding(user, SECTIONS.find((s) => s.key === section))} />;
}

function DashboardPage() {
  const { user } = useAuth();
  if (!dashboardPanelsFor(user).length) return <Navigate replace to="/time/timesheets" />;
  return <DashboardView user={user} />;
}

export default function App() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [headerH, setHeaderH] = useState(48);
  const [currentJob, setCurrentJob] = useState(null);
  // Once per sign-in (and per emulated user): sync jobs and time periods from
  // Navision if that has not happened today. Settings shows the result.
  const [syncResult, setSyncResult] = useState(null);
  const userKey = user ? `${user.userId}:${user.emulating?.resourceNo || ''}` : '';
  useEffect(() => {
    if (!userKey) return undefined;
    let cancelled = false;
    api.checkSync()
      .then((r) => { if (!cancelled && r && r.synced) setSyncResult(r); })
      .catch((e) => { if (!cancelled) setSyncResult({ errors: [e.message], log: [] }); });
    return () => { cancelled = true; };
  }, [userKey]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
        Loading...
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  // Standalone "View Entries" window (opened in a new tab from Create Invoices):
  // full-window, no app chrome.
  if (location.pathname === '/jpl') {
    return <JobPlanningLinesView />;
  }

  // Key on emulated user so pages re-mount when emulation changes
  const emulationKey = user.emulating?.resourceNo || 'self';

  return (
    <HeaderHeightContext.Provider value={headerH}>
    <CurrentJobContext.Provider value={{ job: currentJob, setJob: setCurrentJob }}>
      <AppHeader onHeight={setHeaderH} />
      <div className={`app-container ${user.emulating ? 'emulating' : ''}`} key={emulationKey}>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          <Route path="/time" element={<SectionLanding section="time" />} />
          <Route path="/time/timesheets" element={<TimesheetsPage />} />
          <Route path="/time/timesheets/:id/:view?" element={<TimesheetDetailRoute />} />
          <Route path="/analytics" element={<SectionLanding section="analytics" />} />
          <Route path="/analytics/billable" element={<BillableTime />} />
          <Route path="/analytics/reports" element={<ReportsPage />} />

          <Route path="/expenses/:tab?" element={<ExpensesPage />} />

          <Route path="/jobs" element={<SectionLanding section="jobs" />} />
          <Route path="/jobs/list" element={<JobListPage />} />
          <Route path="/jobs/invoicing" element={<CreateInvoicesPage />} />
          <Route path="/jobs/approval" element={<InvoiceApprovalPage />} />
          <Route path="/jobs/budget" element={<JobBudgetPage />} />
          <Route path="/jobs/:jobNo/:tab?" element={<JobPage />} />

          <Route path="/receivables" element={<ReceivablesPage />} />

          <Route path="/projections" element={<ProjectionsPage />} />
          <Route path="/projections/admin" element={<ProjectionsAdminPage />} />

          <Route path="/settings/:tab?" element={<SettingsPage initialSyncResult={syncResult} />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help-invoicing" element={<InvoicingHelpPage />} />

          {/* Old addresses kept working for links and bookmarks */}
          <Route path="/timesheet/:id" element={<LegacyTimesheetRedirect />} />
          <Route path="/time/billable" element={<Navigate to="/analytics/billable" replace />} />
          <Route path="/navision-web" element={<NavisionWebRedirect />} />
          <Route path="/training" element={<Navigate to="/help" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <div className="version-footer">
          {process.env.REACT_APP_BUILD_VERSION || 'dev'}, {process.env.REACT_APP_BUILD_DATE || 'local'}
        </div>
      </div>
    </CurrentJobContext.Provider>
    </HeaderHeightContext.Provider>
  );
}
